import { put, all, takeLatest, select, fork, delay } from "redux-saga/effects";
import * as actionTypes from "./actionTypes";
import * as authActionTypes from "../auth/actionTypes";
import API from "../../commonUtils/API.js";
import { handleApiError } from "../../commonUtils/Functions";
import { toast } from "react-toastify";
import constants from "../../commonUtils/constants";
import { format } from "date-fns";

function* loadPageDataSaga() {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        const { data } = yield API.get("/uploads/load-page-data");
        if (data.success) {
            yield put({ type: actionTypes.LOAD_PAGE_DATA_SUCCESS, payload: data.data });
        } else {
            toast.error("Could not load page data.");
            yield put({ type: actionTypes.LOAD_PAGE_DATA_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Oops! Something went unexpectedly wrong.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.LOAD_PAGE_DATA_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* getProjectsForMasterSaga({ masterId }) {
    try {
        // yield put({ type: actionTypes.SET_LOADING, payload: true });

        let queryString = `keyword=all&master=${masterId}`;

        const { data } = yield API.get(`/projects?${queryString}`);

        if (data.success) {
            const projectList = data.data.map(project => ({label: project.title, value: project._id}))
            yield put({ type: actionTypes.GET_PROJECTS_FOR_MASTER_SUCCESS, payload: projectList });
        } else {
            toast.error("Oops! Something went unexpectedly wrong.");
            yield put({ type: actionTypes.GET_PROJECTS_FOR_MASTER_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Oops! Something went unexpectedly wrong.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_PROJECTS_FOR_MASTER_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* getUploadLogsSaga({ params }) {
    const { currentPage, perPage } = params;
    try {
        yield put({ type: actionTypes.SET_LIST_LOADING, payload: true });

        const filters = yield select((state) => state.Uploads.filters);

        let queryString = `currentPage=${currentPage}&perPage=${perPage}`;
        if(filters.keyword) queryString = queryString.concat(`&keyword=${encodeURIComponent(filters.keyword)}`);
        if(filters.project) queryString = queryString.concat(`&project=${filters.project}`);
        if(filters.masterProject) queryString = queryString.concat(`&masterProject=${filters.masterProject}`);
        if(filters.sourceType) queryString = queryString.concat(`&sourceType=${filters.sourceType}`);
        if(filters.vendor) queryString = queryString.concat(`&vendor=${filters.vendor}`);
        if(filters.channelPartner) queryString = queryString.concat(`&channelPartner=${filters.channelPartner}`);
        if(filters.spoc) queryString = queryString.concat(`&spoc=${filters.spoc}`);
        if(filters.dataCategory) queryString = queryString.concat(`&dataCategory=${filters.dataCategory}`);
        if(filters.leadSource) queryString = queryString.concat(`&leadSource=${filters.leadSource}`);
        if(filters.startDate) queryString = queryString.concat(`&startDate=${format(filters.startDate, 'yyyy-MM-dd')}`);
        if(filters.endDate) queryString = queryString.concat(`&endDate=${format(filters.endDate, 'yyyy-MM-dd')}`);
        if(filters.pending) queryString = queryString.concat(`&pending=${filters.pending}`);
        
        const { data } = yield API.get(`/uploads/logs?${queryString}`);
        
        if (data.success) {
            yield put({ type: actionTypes.GET_UPLOAD_LOGS_SUCCESS, payload: data.data });
        } else {
            toast.error("Could not load upload logs.");
            console.error("Error occurred while loading page data:", data);
            yield put({ type: actionTypes.GET_UPLOAD_LOGS_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not load upload logs.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_UPLOAD_LOGS_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* uploadFilesSaga({ payload }) {
    const { uploadData, uploadFile } = payload;
    
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        yield put({ type: actionTypes.SET_LOADING_TEXT, payload: "Uploading File..." });
 
        // Create a FormData object
        const formData = new FormData();
       
        Object.entries(uploadData).forEach(([key, value]) => {
            formData.append(key, value);
        });
        formData.append("file", uploadFile);

        const { data } = yield API.post(uploadData?._id || uploadData?.id ? `/uploads/file-upload/${uploadData?._id || uploadData?.id}` : `/uploads/file-upload`, formData,
            { headers: { "Content-Type": "multipart/form-data" } }
        );
 
        if (data.success) {
            const pagination = yield select((state) => state.Uploads.UploadLogData.pagination);
            yield put({ type: actionTypes.UPLOAD_FILES_SUCCESS, payload: data.data });
            toast.success("File uploaded successfully!");
            yield put({ type: actionTypes.GET_UPLOAD_LOGS, params: { currentPage: pagination.currentPage, perPage: pagination.perPage } });
        } else {
            toast.error("Error while uploading files.");
            yield put({ type: actionTypes.UPLOAD_FILES_FAILURE, payload: data });
        }
 
    } catch (error) {
        handleApiError(error, "Error while uploading files.");
        const data = error?.response?.data;
        toast.error("Error while uploading files.");
        yield put({ type: actionTypes.UPLOAD_FILES_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

export function* updateUploadLogDataSaga(newLogData) {
    const { field_mapping, etl_process, status } = newLogData;
    const uploadLogData = yield select((state) => state.Uploads.UploadLogData);

    const updatedData = uploadLogData.data.map(log => {
        if (log._id === newLogData.id || log._id === newLogData._id) {
            return {
                ...log,
                field_mapping: {
                    ...log.field_mapping,
                    status: field_mapping.status
                },
                etl_process: {
                    ...log.etl_process,
                    status: etl_process.status
                },
                status: status
            };
        }
        return log;
    });

    yield put({ type: actionTypes.GET_UPLOAD_LOGS_SUCCESS, payload: { ...uploadLogData, data: updatedData } });
};

function* getUploadLogDataSaga({ payload }) {
    const {key, navigate=null, path=""} = payload;
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const apiUrl = constants.upload.simulated ? `/uploads/logs/simulated/${key}` : `/uploads/logs/${key}`;
        const { data } = yield API.get(apiUrl);
        const statuses = ["triggered", "processing"];
        const { field_mapping, etl_process } = data.data.uploadLog;
        
        if (data.success) {
            if (statuses.includes(field_mapping.status) || statuses.includes(etl_process.status)) {
                toast.info("Process is still running")
            } else {
                if(navigate && path) navigate(path)
                else if(data.status === 403 && navigate) {
                    navigate("/upload-history");
                } else {
                    const currentRoute = window.location.pathname;
                    if(data?.data?.uploadLog && currentRoute === "/upload-history") {
                        yield* updateUploadLogDataSaga(data.data.uploadLog);
                    }
                }
            }
            yield put({ type: actionTypes.GET_UPLOAD_LOG_DATA_SUCCESS, payload: data.data });
        } else {
            toast.error("Could not load upload logs.");
            console.error("Error occurred while loading page data:", data);
            yield put({ type: actionTypes.GET_UPLOAD_LOG_DATA_FAILURE, payload: data });
            if(data.message === "Upload Log not found"){
                yield put({ type: authActionTypes.SET_SHOW_NOT_FOUND_PAGE, payload: {} });
            }
        }
    } catch (error) {
        const data = error?.response?.data;
        if (error?.response?.status === 403) {
            toast.error(data?.message || "You do not have permission to update this upload log.");
            if(navigate) navigate("/upload-history");
        } else {
            handleApiError(error, "Could not load upload logs.");
            yield put({ type: actionTypes.GET_UPLOAD_LOG_DATA_FAILURE, payload: data || { message: "Internal server error" } });
            if(data.message === "Upload Log not found"){
                yield put({ type: authActionTypes.SET_SHOW_NOT_FOUND_PAGE, payload: {} });
            }
        }
    }
};

function* viewUploadLogDataSaga({ payload }) {
    const { key, navigate=null, path='' } = payload;
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const apiUrl = `/uploads/logs/view/${key}`;
        const { data } = yield API.get(apiUrl);
        const statuses = ["Processing Submission", "Processing Field Mapping"];
        
        if (data.success) {
            if (statuses.includes(data.data.status)) {
                toast.info("Process is still running");
                if(navigate) navigate("/upload-history");
            } else {
                if(navigate && path) navigate(path);
                yield put({ type: actionTypes.VIEW_UPLOAD_LOG_SUCCESS, payload: data.data });
            }
        } else {
            toast.error("Could not load upload log.");
            console.error("Error occurred while loading page data:", data);
            yield put({ type: actionTypes.VIEW_UPLOAD_LOG_FAILURE, payload: data });
            if(data.message === "Upload Log not found"){
                yield put({ type: authActionTypes.SET_SHOW_NOT_FOUND_PAGE, payload: {} });
            }
        }

    } catch (error) {
        handleApiError(error, "Could not load upload logs.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_UPLOAD_LOG_DATA_FAILURE, payload: data || { message: "Internal server error" } });
        if(data.message === "Upload Log not found"){
            yield put({ type: authActionTypes.SET_SHOW_NOT_FOUND_PAGE, payload: {} });
        }
    }
};

function* changeFileSaga({ payload }) {
    const { id, uploadData, uploadFile } = payload;

    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        yield put({ type: actionTypes.SET_LOADING_TEXT, payload: "Updating File..." });
        
        if (!id) {
            toast.error("File ID missing");
            yield put({ type: actionTypes.CHANGE_FILE_FAILURE, payload: { message: "File ID missing" } });
            return;
        }

        // Create a FormData object
        const formData = new FormData();
       
        // Append all upload data fields
        Object.entries(uploadData).forEach(([key, value]) => {
            if (key !== '_id' && key !== 'id') { // Exclude ID fields from form data
                formData.append(key, value);
            }
        });
        
        // Append file only if it exists (file is optional for change operation)
        if (uploadFile) {
            formData.append("file", uploadFile);
        }

        // Make API call with FormData and proper headers
        const { data } = yield API.post(`/uploads/change-file/${id}`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
        });
 
        if (data.success) {
            const pagination = yield select((state) => state.Uploads.UploadLogData.pagination);
            
            toast.success(uploadFile ? "File changed successfully!" : "Data updated successfully!");
            yield put({ type: actionTypes.CHANGE_FILE_SUCCESS, payload: data.data });
            
            // Refresh the upload logs list
            yield put({ 
                type: actionTypes.GET_UPLOAD_LOGS, 
                params: { currentPage: pagination.currentPage, perPage: pagination.perPage } 
            });
        } else {
            toast.error(data.message || "Failed to update the file.");
            yield put({ type: actionTypes.CHANGE_FILE_FAILURE, payload: data });
        }
    } catch (error) {
        handleApiError(error, "Could not update the file.");
        const data = error?.response?.data;
        toast.error(data?.message || "Could not update the file.");
        yield put({ type: actionTypes.CHANGE_FILE_FAILURE, payload: data || { message: "Internal server error" } });
    } finally {
        yield put({ type: actionTypes.SET_LOADING, payload: false });
        yield put({ type: actionTypes.SET_LOADING_TEXT, payload: "" });
    }
}

function* getMappedFieldsSaga({ payload }) {
    const {id} = payload;
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        yield put({ type: actionTypes.SET_LOADING_TEXT, payload: "Field Mapping in progress..." });
        // const { data } = yield API.get(`/uploads/mapped-fields/${id}`);
        const apiUrl = constants.upload.simulated ? `/uploads/mapped-fields/simulated/${id}` : `/uploads/mapped-fields/${id}`
        const { data } = yield API.get(apiUrl);
        const fieldMappingTimeoutId = yield select((state) => state.Uploads.fieldMappingTimeoutId);
        clearTimeout(fieldMappingTimeoutId);
        if (data.success) {
            if (data?.data?.uploadLog?.field_mapping?.status === "failed") {
                toast.error("Field mapping failed. Please try again.");
                // navigate("/upload-history");
                yield put({ type: actionTypes.GET_MAPPED_FIELDS_FAILURE, payload: {data: data?.data, message: "Field mapping failed"} });
            } else {
                yield put({ type: actionTypes.GET_MAPPED_FIELDS_SUCCESS, payload: data.data });
            }
        } else {
            toast.error("Field mapping failed. Please try again.");
            console.error("Error occurred while mapping fields:", data);
            yield put({ type: actionTypes.GET_MAPPED_FIELDS_FAILURE, payload: data });
        }
    } catch (error) {
        toast.error("Field mapping failed. Please try again.");
        handleApiError(error, "Field Mapping Failed");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_MAPPED_FIELDS_FAILURE, payload: { message: data?.message || "Internal server error" } });
    }
};

function* updateMetadataSaga({ payload }) {
    const { id, formData } = payload;
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        // yield put({ type: actionTypes.SET_LOADING_TEXT, payload: "Updating Metadata..." });

        const { data } = yield API.patch(`/uploads/update-metadata/${id}`, formData);
        if (data.success) {
            yield put({ type: actionTypes.UPDATE_METADATA_SUCCESS, payload: data.data });
            // toast.success("Metadata updated successfully!");
        } else {
            toast.error("Error while updating metadata.");
            yield put({ type: actionTypes.UPDATE_METADATA_FAILURE, payload: data });
        }
 
    } catch (error) {
        handleApiError(error, "Error while updating metadata.");
        const data = error?.response?.data;
        toast.error("Error while updating metadata.");
        yield put({ type: actionTypes.UPDATE_METADATA_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* navigateToHistoryPage(navigate, pagination) {
    yield fork(function* () {
        yield delay(2000);
        yield put({ 
            type: actionTypes.GET_UPLOAD_LOGS, 
            params: { currentPage: pagination.currentPage, perPage: pagination.perPage }
        });
        navigate('/upload-history'); 
    });
}

function* runETLProcessSaga({ payload }) {
    const { id, mappedColumns="", additionalColumns="", navigate } = payload;
    try {
        const apiUrl = constants.upload.simulated ? `/uploads/trigger-etl-service/simulated/${id}` : `/uploads/trigger-etl-service/${id}`
        const apiRequest = API.post(apiUrl, { mappedColumns, additionalColumns });
        const pagination = yield select((state) => state.Uploads.UploadLogData.pagination);
        yield navigateToHistoryPage(navigate, pagination);

        const { data } = yield apiRequest;
        if (data.success) {
            yield put({ type: actionTypes.RUN_ETL_PROCESS_SUCCESS, payload: data.data });
            const currentRoute = window.location.pathname;
            if (currentRoute === "/upload-history") {
                yield* updateUploadLogDataSaga(data.data);
            }
        } else {
            toast.error("Could not load upload logs.");
            console.error("Error occurred while loading page data:", data);
            yield put({ type: actionTypes.RUN_ETL_PROCESS_FAILURE, payload: data });
        }
    } catch (error) {
        if (error?.response?.status === 404) {
            toast.error("Could not load upload logs.");
        } else if (error?.response?.status === 408) {
            toast.error("ETL Process timed out. Please refresh the log status.");
        } else {
            handleApiError(error, "Could not load upload logs.");
            toast.error("Something went wrong. Please try again.");
        }
        const data = error?.response?.data;
        yield put({ type: actionTypes.RUN_ETL_PROCESS_FAILURE, payload: data || { message: "Internal server error" } });
    }
}

function* rejectReviewSaga({ payload }) {
    try {
        const { id, remarks, navigate } = payload;
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        const apiUrl = constants.upload.simulated ? `/uploads/reject-review/simulated/${id}` : `/uploads/reject-review/${id}`;
        const pagination = yield select((state) => state.Uploads.UploadLogData.pagination);
        yield navigateToHistoryPage(navigate, pagination);

        const { data } = yield API.post(apiUrl, { remarks });
        if (data.success) {
            yield put({ type: actionTypes.REJECT_REVIEW_SUCCESS });
            toast.success(data.message);
        } else {
            const msg = data.message || "Something went wrong. Please try again."
            toast.error(msg);
            yield put({ type: actionTypes.REJECT_REVIEW_FAILURE, message: msg });
        }
    } catch (error) {
        const data = error?.response?.data;
        const msg = data.message || "Something went wrong. Please try again."
        handleApiError(error, msg);
        toast.error(msg)
        yield put({ type: actionTypes.REJECT_REVIEW_FAILURE, message: "Something went wrong. Please try again." });
    }
};

function* downloadFileSaga({ payload }) {
    const { id, originalFileName } = payload;
    try {
        yield put({ type: actionTypes.SET_DOWNLOAD_LOADING, payload: true });

        const response = yield API.get(`/uploads/download-file/${id}`, { responseType: 'blob' });

        if (response.data) {
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = originalFileName; // Use the original file name
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            yield put({ type: actionTypes.DOWNLOAD_FILE_SUCCESS });
        } else {
            toast.error("Failed to download the file.");
            yield put({ type: actionTypes.DOWNLOAD_FILE_FAILURE, message: "Failed to download the file." });
        }

    } catch (error) {
        handleApiError(error, "Failed to download the file.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.DOWNLOAD_FILE_FAILURE, payload: data || { message: "Internal server error" } });
    } finally {
        yield put({ type: actionTypes.SET_DOWNLOAD_LOADING, payload: false });
    }
};

function* UploadsSaga() {
  yield all([
    takeLatest(actionTypes.LOAD_PAGE_DATA, loadPageDataSaga),
    takeLatest(actionTypes.GET_UPLOAD_LOGS, getUploadLogsSaga),
    takeLatest(actionTypes.GET_UPLOAD_LOG_DATA, getUploadLogDataSaga),
    takeLatest(actionTypes.VIEW_UPLOAD_LOG, viewUploadLogDataSaga),
    takeLatest(actionTypes.UPLOAD_FILES, uploadFilesSaga),
    takeLatest(actionTypes.CHANGE_FILE_REQUEST, changeFileSaga),
    takeLatest(actionTypes.GET_MAPPED_FIELDS, getMappedFieldsSaga),
    takeLatest(actionTypes.UPDATE_METADATA, updateMetadataSaga),
    takeLatest(actionTypes.GET_PROJECTS_FOR_MASTER, getProjectsForMasterSaga),
    takeLatest(actionTypes.RUN_ETL_PROCESS, runETLProcessSaga),
    takeLatest(actionTypes.REJECT_REVIEW, rejectReviewSaga),
    takeLatest(actionTypes.DOWNLOAD_FILE, downloadFileSaga),
  ]);
}

export default UploadsSaga;
